import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { EASE } from '../motion/Reveal';
import { useIsMobile } from '../../hooks/useIsMobile';
import { heroSlides } from './heroSlides';
import { FloatingMerch } from './FloatingMerch';

export const HeroCarousel = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroRef = useRef(null);
  const isMobile = useIsMobile();
  const reduceMotion = useReducedMotion();
  const parallaxOn = !isMobile && !reduceMotion;
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const heroTextOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length), 8000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  const slide = heroSlides[currentSlide];

  return (
    <section ref={heroRef} className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-testid="hero-section">
      {heroSlides.map((s, index) => (
        <div key={s.image} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
          <motion.div className="hero-background" style={parallaxOn ? { y: heroY, scale: 1.08 } : undefined}>
            <picture>
              {s.mobileImage && <source media="(max-width: 1023px)" srcSet={s.mobileImage} />}
              <img src={s.image} alt="Professional printing services" className={`hero-image ${s.bright ? 'hero-image-bright' : ''}`} />
            </picture>
            <div className={`hero-overlay ${s.bright ? 'hero-overlay-soft' : ''}`} />
          </motion.div>
        </div>
      ))}

      <motion.div className="container mx-auto px-5 md:px-10 relative z-20" style={parallaxOn ? { y: heroTextY, opacity: heroTextOpacity } : undefined}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={currentSlide}
            className={slide.feature ? 'grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] gap-10 lg:gap-16 items-center' : 'max-w-4xl'}
            initial={reduceMotion ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -16 }}
            transition={{ duration: 0.7, ease: EASE }}
            data-testid="hero-content"
          >
            <div className="max-w-4xl">
              {slide.badges && (
                <div className="flex flex-wrap gap-2 mb-6" data-testid="hero-badges">
                  {slide.badges.map((badge) => (
                    <span key={badge.en} className="inline-block text-[11px] md:text-xs uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full border border-white/20 bg-gradient-to-r from-cyan-400/10 via-purple-500/10 to-yellow-400/10 text-white backdrop-blur-sm">
                      {t(badge)}
                    </span>
                  ))}
                </div>
              )}
              <h1 className="hero-title-smaller mb-8 lg:whitespace-pre-line" style={{ fontFamily: "'Exo 2', sans-serif" }}>{t(slide.title)}</h1>
              <p className="body-large text-[white] mb-12 max-w-2xl">{t(slide.description)}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={slide.buttonLink}>
                  <Button className="btn-primary min-w-[200px]">
                    {t(slide.buttonText)}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                {slide.secondaryLink && (
                  <Link to={slide.secondaryLink}>
                    <Button className="btn-secondary min-w-[200px]" data-testid="hero-secondary-btn">
                      {t(slide.secondaryText)}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
            {slide.feature && (
              <div className="hidden lg:block relative" data-testid="hero-feature-image">
                <FloatingMerch />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex items-center gap-4">
        <button onClick={prevSlide} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="Previous slide">
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <div className="flex gap-2">
          {heroSlides.map((s, index) => (
            <button
              key={s.image}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-white w-8' : 'bg-white/50'}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        <button onClick={nextSlide} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="Next slide">
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>
    </section>
  );
};
